import {
  cloneJsonObject,
  isJsonValueEqual,
  normalizeJsonObject,
  type JsonObject,
  type JsonValue,
} from './shared/jsonValues'

type ParamValue = JsonValue
type ParamValues = JsonObject
export type ParamChangeEvent = {
  instanceId: string
  params: ParamValues
  previousParams: ParamValues | null
  changedKeys: string[]
  revision: number
  isInitial: boolean
}
type ParamChangeListener = (event: ParamChangeEvent) => void
type ParamUpdateInterceptor = (event: ParamChangeEvent) => Promise<void> | void

type ParamsMessage =
  | {
      source: 'asset-params'
      type: 'ready'
      instanceId: string
    }
  | {
      source: 'asset-params'
      type: 'update'
      target: string
      values?: Record<string, unknown>
      revision?: number
    }

const windowsByInstance = new Map<string, Window>()
const paramsByInstance = new Map<string, ParamValues>()
const revisionByInstance = new Map<string, number>()
const readyWindowByInstance = new Map<string, Window>()
const listenersByInstance = new Map<string, Set<ParamChangeListener>>()
const instancesWithHostUpdates = new Set<string>()
const interceptorsByInstance = new Map<string, ParamUpdateInterceptor>()
const updateChainsByInstance = new Map<string, Promise<void>>()

let listenerAttached = false

const normalizeValues = (values?: Record<string, unknown> | null): ParamValues => normalizeJsonObject(values)

const cloneParams = (values?: ParamValues | null): ParamValues => cloneJsonObject(values)

const getChangedKeys = (previousValues: ParamValues, nextValues: ParamValues) => {
  const keys = new Set([...Object.keys(previousValues), ...Object.keys(nextValues)])
  const changedKeys: string[] = []
  keys.forEach((key) => {
    const previousValue = previousValues[key]
    const nextValue = nextValues[key]
    const changed = !isJsonValueEqual(previousValue, nextValue)
    if (changed) changedKeys.push(key)
  })
  return changedKeys
}

const notifySubscribers = (
  instanceId: string,
  previousValues: ParamValues,
  nextValues: ParamValues,
  revision: number
) => {
  const listeners = listenersByInstance.get(instanceId)
  const hadPreviousUpdate = instancesWithHostUpdates.has(instanceId)
  instancesWithHostUpdates.add(instanceId)
  if (!listeners || listeners.size === 0) return

  const event: ParamChangeEvent = {
    instanceId,
    params: cloneParams(nextValues),
    previousParams: hadPreviousUpdate ? cloneParams(previousValues) : null,
    changedKeys: getChangedKeys(previousValues, nextValues),
    revision,
    isInitial: !hadPreviousUpdate,
  }

  listeners.forEach((listener) => listener(event))
}

const notifyInstance = (instanceId: string) => {
  const targetWindow = windowsByInstance.get(instanceId)
  if (!targetWindow) return
  const readyWindow = readyWindowByInstance.get(instanceId)
  if (readyWindow !== targetWindow) return
  if (!revisionByInstance.has(instanceId)) return
  const values = paramsByInstance.get(instanceId) ?? {}
  const revision = revisionByInstance.get(instanceId) ?? 0
  targetWindow.postMessage(
    {
      source: 'asset-params',
      type: 'update',
      target: instanceId,
      values,
      revision,
    },
    '*'
  )
}

const setParamsForInstance = (
  instanceId: string,
  values?: Record<string, unknown> | null,
  options?: { revision?: number }
) => {
  const nextValues = normalizeValues(values)
  const explicitRevision = options?.revision
  const previousChain = updateChainsByInstance.get(instanceId) ?? Promise.resolve()

  const nextChain = previousChain
    .catch(() => undefined)
    .then(async () => {
      const previousValues = cloneParams(paramsByInstance.get(instanceId))
      const hasPreviousUpdate = instancesWithHostUpdates.has(instanceId)
      const revision =
        typeof explicitRevision === 'number' && Number.isFinite(explicitRevision) && explicitRevision >= 0
          ? Math.trunc(explicitRevision)
          : (revisionByInstance.get(instanceId) ?? 0) + 1
      const event: ParamChangeEvent = {
        instanceId,
        params: cloneParams(nextValues),
        previousParams: hasPreviousUpdate ? previousValues : null,
        changedKeys: getChangedKeys(previousValues, nextValues),
        revision,
        isInitial: !hasPreviousUpdate,
      }

      const interceptor = interceptorsByInstance.get(instanceId)
      if (interceptor) {
        await interceptor(event)
      }

      paramsByInstance.set(instanceId, cloneParams(nextValues))
      revisionByInstance.set(instanceId, revision)
      notifySubscribers(instanceId, previousValues, nextValues, revision)
      notifyInstance(instanceId)
    })
    .catch((error) => {
      console.error('[asset-params] failed to apply params update', error)
    })

  updateChainsByInstance.set(instanceId, nextChain)
  return nextChain
}

const registerInstance = (instanceId: string, targetWindow: Window) => {
  if (!instanceId) return
  const previous = windowsByInstance.get(instanceId)
  windowsByInstance.set(instanceId, targetWindow)
  if (previous !== targetWindow) {
    readyWindowByInstance.delete(instanceId)
  }
  notifyInstance(instanceId)
}

const unregisterInstance = (instanceId: string, targetWindow?: Window | null) => {
  if (!instanceId) return
  const current = windowsByInstance.get(instanceId)
  if (!current) return
  if (!targetWindow || targetWindow === current) {
    windowsByInstance.delete(instanceId)
    readyWindowByInstance.delete(instanceId)
  }
}

const markInstanceReady = (instanceId: string, sourceWindow: Window) => {
  if (!instanceId) return
  windowsByInstance.set(instanceId, sourceWindow)
  readyWindowByInstance.set(instanceId, sourceWindow)
  notifyInstance(instanceId)
}

const forwardReadyToParent = (instanceId: string, sourceWindow: Window) => {
  if (!window.parent || window.parent === window) return
  if (sourceWindow === window.parent) return
  window.parent.postMessage(
    {
      source: 'asset-params',
      type: 'ready',
      instanceId,
    },
    '*'
  )
}

const handleMessage = (event: MessageEvent) => {
  const data = event.data as ParamsMessage | undefined
  if (!data || data.source !== 'asset-params') return
  if (data.type === 'ready' && typeof data.instanceId === 'string') {
    const sourceWindow = event.source instanceof Window ? event.source : windowsByInstance.get(data.instanceId) ?? null
    if (sourceWindow) {
      markInstanceReady(data.instanceId, sourceWindow)
      forwardReadyToParent(data.instanceId, sourceWindow)
    }
    return
  }
  if (data.type === 'update' && typeof data.target === 'string') {
    setParamsForInstance(data.target, data.values ?? null, { revision: data.revision })
  }
}

const ensureListener = () => {
  if (listenerAttached) return
  window.addEventListener('message', handleMessage)
  listenerAttached = true
}

if (typeof window !== 'undefined') {
  ensureListener()
}

export type { ParamValue, ParamValues, ParamChangeListener }
export const assetParamsBus = {
  ensureListener,
  registerInstance,
  unregisterInstance,
  setParamsForInstance,
  getParamsForInstance: (instanceId: string) => cloneParams(paramsByInstance.get(instanceId)),
  subscribe: (instanceId: string, listener: ParamChangeListener) => {
    const listeners = listenersByInstance.get(instanceId) ?? new Set<ParamChangeListener>()
    listeners.add(listener)
    listenersByInstance.set(instanceId, listeners)
    if (revisionByInstance.has(instanceId)) {
      const currentParams = cloneParams(paramsByInstance.get(instanceId))
      const revision = revisionByInstance.get(instanceId) ?? 0
      queueMicrotask(() => {
        listener({
          instanceId,
          params: currentParams,
          previousParams: null,
          changedKeys: Object.keys(currentParams),
          revision,
          isInitial: true,
        })
      })
    }
    return () => {
      const currentListeners = listenersByInstance.get(instanceId)
      if (!currentListeners) return
      currentListeners.delete(listener)
      if (currentListeners.size === 0) listenersByInstance.delete(instanceId)
    }
  },
  setUpdateInterceptor: (instanceId: string, interceptor?: ParamUpdateInterceptor) => {
    if (!instanceId) return () => undefined
    if (interceptor) interceptorsByInstance.set(instanceId, interceptor)
    else interceptorsByInstance.delete(instanceId)
    return () => {
      const current = interceptorsByInstance.get(instanceId)
      if (current === interceptor) interceptorsByInstance.delete(instanceId)
    }
  },
}
